/**
 * The destinations catalog — every place in Conway a person can go to on
 * purpose, as data.
 *
 * Single source for tab identity (`WorkspaceTab.destinationId`), the starred
 * rail, and the All-areas launcher. Hand-maintained like `hubConfig.ts`; NOT
 * derived from `appRoutes.tsx`, which is the secondary-pane route table and
 * carries legacy redirects. Lives in `shared` because `features/workspace`
 * must import it and `features → app` would be an upward import.
 *
 * Invariant (pinned by test): one destination per path, and every entry's
 * own `path` resolves back to itself.
 */
import { ATTENTION_ROUTES } from '@shared/constants/routes';

import { TYPO_RANK, matchRank } from './destinationMatch';
import { learnedIds, loadGoPicks } from './goPicks';

import type { GoPick } from './goPicks';
import type { DomainKey, Icons } from '@shared/components/ui';

/** One of the six sections All-areas and the launcher group destinations into. */
export type DestinationGroup = 'you' | 'messages' | 'create' | 'plan' | 'knowledge' | 'manage';

/** A single place in the app: one row in the catalog, one possible tab identity. */
export interface Destination {
  /** Stable id — never a path, so a route rename does not silently drop a star. */
  id: string;
  label: string;
  /** One line under the label on an All-areas card. */
  description: string;
  icon: keyof typeof Icons;
  /** When set, the rail renders the duotone domain-colored icon. */
  domain?: DomainKey;
  path: string;
  /**
   * When set, paths at or under this base also resolve to this destination
   * (the row's own `path` stays the click target). Mirrors navConfig's
   * `activeBasePath`.
   */
  activeBasePath?: string;
  group: DestinationGroup;
}

/** Group order as rendered on All areas. */
export const DESTINATION_GROUPS: readonly { id: DestinationGroup; label: string }[] = [
  { id: 'you', label: 'You' },
  { id: 'messages', label: 'Messages' },
  { id: 'create', label: 'Create' },
  { id: 'plan', label: 'Plan' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'manage', label: 'Manage' },
];

/** Ordered as rendered on All areas; the group comments are the section breaks. */
export const DESTINATIONS: readonly Destination[] = [
  // ── You ──
  {
    id: 'home',
    label: 'Home',
    description: 'Your home base.',
    icon: 'Home',
    domain: 'home',
    path: '/home',
    group: 'you',
  },
  {
    id: 'chat',
    label: 'Chat',
    description: 'Talk to Conway.',
    icon: 'Sparkles',
    domain: 'chat',
    path: '/chat',
    activeBasePath: '/chat',
    group: 'you',
  },
  // Every past chat by date. The Chat tab's own sidebar covers recents; this is
  // the full archive, and a phone (which has no sidebar) reaches it from here.
  {
    id: 'conversations',
    label: 'All Conversations',
    description: 'Every chat, by date.',
    icon: 'History',
    path: '/conversations',
    group: 'you',
  },
  // ── Messages ──
  {
    id: 'inbox',
    label: 'Inbox',
    description: 'What needs you.',
    icon: 'Mail',
    domain: 'inbox',
    path: ATTENTION_ROUTES.root,
    group: 'messages',
  },
  {
    id: 'starred',
    label: 'Starred',
    description: 'To process later.',
    icon: 'Star',
    domain: 'starred',
    path: ATTENTION_ROUTES.starred,
    group: 'messages',
  },
  {
    id: 'reading-room',
    label: 'Reading Room',
    description: 'Longform you saved.',
    icon: 'Books',
    domain: 'later',
    path: ATTENTION_ROUTES.readingRoom,
    group: 'messages',
  },
  {
    id: 'paper-trail',
    label: 'Paper Trail',
    description: 'All mail, filed.',
    icon: 'Archive',
    path: ATTENTION_ROUTES.mailAll,
    group: 'messages',
  },
  {
    id: 'bulletin-board',
    label: 'Bulletin Board',
    description: 'Need to know, not act.',
    icon: 'Pin',
    path: ATTENTION_ROUTES.bulletinBoard,
    group: 'messages',
  },
  {
    id: 'drafts',
    label: 'Drafts',
    description: 'Unsent mail.',
    icon: 'FileText',
    path: ATTENTION_ROUTES.mailDrafts,
    group: 'messages',
  },
  {
    id: 'dismissed-inboxes',
    label: 'History',
    description: 'Inboxes you cleared.',
    icon: 'ReadLater',
    path: ATTENTION_ROUTES.history,
    group: 'messages',
  },
  {
    id: 'labels',
    label: 'Labels',
    description: 'Gmail labels.',
    icon: 'Tags',
    path: ATTENTION_ROUTES.mailLabels,
    group: 'messages',
  },
  {
    id: 'sent',
    label: 'Sent',
    description: 'Mail you sent.',
    icon: 'PaperPlaneTop',
    path: ATTENTION_ROUTES.mailSent,
    group: 'messages',
  },
  {
    id: 'spam',
    label: 'Spam',
    description: 'Caught by the filter.',
    icon: 'Warning',
    path: ATTENTION_ROUTES.mailSpam,
    group: 'messages',
  },
  {
    id: 'email-settings',
    label: 'Email Settings',
    description: 'Rules and blocked senders.',
    icon: 'Settings',
    path: ATTENTION_ROUTES.mailSettings,
    group: 'messages',
  },
  // ── Create ──
  {
    id: 'notes',
    label: 'Notes',
    description: 'Documents.',
    icon: 'MemoPad',
    domain: 'notes',
    path: '/notes/editor',
    activeBasePath: '/notes',
    group: 'create',
  },
  {
    id: 'journal',
    label: 'Journal',
    description: 'Daily entries.',
    icon: 'BookUser',
    domain: 'journal',
    path: '/journal',
    group: 'create',
  },
  {
    id: 'study',
    label: 'Study',
    description: 'Boards, ideas and the writing surface.',
    icon: 'LampDesk',
    domain: 'study',
    path: '/study',
    group: 'create',
  },
  {
    id: 'research',
    label: 'Research',
    description: 'Collections and sources.',
    icon: 'BookSparkles',
    domain: 'research',
    path: '/research',
    group: 'create',
  },
  // ── Plan ──
  {
    id: 'agenda',
    label: 'Agenda',
    description: 'Reminders and events, next four weeks.',
    icon: 'Calendar',
    path: '/agenda',
    group: 'plan',
  },
  {
    id: 'tasks',
    label: 'Tasks',
    description: 'Lists and to-dos.',
    icon: 'ClipboardCheck',
    domain: 'tasks',
    path: '/tasks',
    group: 'plan',
  },
  {
    id: 'food-diary',
    label: 'Food Diary',
    description: 'Nutrition log.',
    icon: 'AppleWhole',
    domain: 'food-diary',
    path: '/food-diary',
    group: 'plan',
  },
  {
    id: 'my-foods',
    label: 'My Foods',
    description: 'Foods you log.',
    icon: 'Tag',
    path: '/food-diary/foods',
    group: 'plan',
  },
  {
    id: 'meals',
    label: 'Meals Planning',
    description: 'This week.',
    icon: 'Utensils',
    domain: 'meals',
    path: '/meals',
    group: 'plan',
  },
  {
    id: 'meals-foods',
    label: 'Meal Foods',
    description: 'Ingredients and dishes.',
    icon: 'Tag',
    path: '/meals/foods',
    group: 'plan',
  },
  {
    id: 'trip-menus',
    label: 'Trip Menus',
    description: 'Menus for travel.',
    icon: 'Globe',
    path: '/meals/trips',
    group: 'plan',
  },
  {
    id: 'meals-settings',
    label: 'Meals Settings',
    description: 'Planning preferences.',
    icon: 'Gears',
    path: '/meals/settings',
    group: 'plan',
  },
  {
    id: 'blocked-days',
    label: 'Blocked Days',
    description: 'Days not to plan.',
    icon: 'Ban',
    path: '/meals/blocked-days',
    group: 'plan',
  },
  {
    id: 'financial',
    label: 'Financial',
    description: 'Overview.',
    icon: 'CircleDollar',
    domain: 'financial',
    path: '/financial/overview',
    group: 'plan',
  },
  {
    id: 'budget',
    label: 'Budget',
    description: 'Monthly budget.',
    icon: 'Calculator',
    path: '/financial/budget',
    group: 'plan',
  },
  {
    id: 'cash-flow',
    label: 'Cash Flow',
    description: 'Money in, money out.',
    icon: 'ArrowRightArrowLeft',
    path: '/financial/cash-flow',
    group: 'plan',
  },
  {
    id: 'retirement',
    label: 'Retirement',
    description: 'Long-range.',
    icon: 'UmbrellaBeach',
    path: '/financial/retirement',
    group: 'plan',
  },
  {
    id: 'debt-savings',
    label: 'Debt & Savings',
    description: 'Balances and goals.',
    icon: 'PiggyBank',
    path: '/financial/debt-savings',
    group: 'plan',
  },
  {
    id: 'jobs',
    label: 'Jobs',
    description: 'Income sources.',
    icon: 'Briefcase',
    path: '/financial/jobs',
    group: 'plan',
  },
  {
    id: 'transactions',
    label: 'Transactions',
    description: 'Every line.',
    icon: 'DollarSign',
    path: '/financial/transactions',
    group: 'plan',
  },
  {
    id: 'investments',
    label: 'Investments',
    description: 'Holdings.',
    icon: 'ChartPie',
    path: '/financial/investments',
    group: 'plan',
  },
  {
    id: 'reports',
    label: 'Reports',
    description: 'Financial reports.',
    icon: 'ChartBar',
    path: '/financial/reports',
    group: 'plan',
  },
  // ── Knowledge ──
  {
    id: 'contacts',
    label: 'Contacts',
    description: 'People the assistant knows.',
    icon: 'UserGroup',
    path: '/my-data/contacts',
    group: 'knowledge',
  },
  {
    id: 'wellness',
    label: 'Wellness',
    description: 'Health and habits.',
    icon: 'Heart',
    path: '/my-data/wellness',
    group: 'knowledge',
  },
  {
    id: 'memories',
    label: 'Memories',
    description: 'What the assistant remembers.',
    icon: 'Memories',
    path: '/settings/assistant/memories',
    group: 'knowledge',
  },
  {
    id: 'household',
    label: 'Household',
    description: 'Shared household knowledge.',
    icon: 'Home',
    path: '/household',
    group: 'knowledge',
  },
  {
    id: 'texts',
    label: 'Messages',
    description: 'Texts and chat history.',
    icon: 'MessageSquare',
    path: '/messages',
    group: 'knowledge',
  },
  // ── Manage ──
  {
    id: 'settings',
    label: 'Settings',
    description: 'You, your assistant, the system.',
    icon: 'Gears',
    path: '/settings/you/profile',
    activeBasePath: '/settings',
    group: 'manage',
  },
  {
    id: 'areas',
    label: 'All areas',
    description: 'Everything Conway does.',
    icon: 'EllipsisStroke',
    path: '/areas',
    group: 'manage',
  },
];

/** The rail on day one. A default, not a decision — the user re-stars freely. */
export const DEFAULT_STAR_IDS: readonly string[] = [
  'inbox',
  'study',
  'journal',
  'chat',
  'notes',
  'tasks',
  'food-diary',
];

const BY_ID = new Map(DESTINATIONS.map(d => [d.id, d]));

/** Looks up a destination by its stable id, or `undefined` if none matches. */
export function destinationById(id: string): Destination | undefined {
  return BY_ID.get(id);
}

/** True when `path` is `base` or a segment-boundary descendant of it. */
export function isAtOrUnder(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`);
}

/**
 * The destination a path belongs to: the entry whose match base
 * (`activeBasePath` when set, else `path`) is the longest segment-boundary
 * prefix of it. Query and hash are ignored. `undefined` when no entry covers
 * the path.
 */
export function destinationForPath(fullPath: string): Destination | undefined {
  const pathname = fullPath.split(/[?#]/)[0] ?? '';
  let best: Destination | undefined;
  let bestBase = '';
  for (const d of DESTINATIONS) {
    const base = d.activeBasePath ?? d.path;
    if (!isAtOrUnder(pathname, base)) continue;
    if (!best || base.length > bestBase.length) {
      best = d;
      bestBase = base;
    }
  }
  return best;
}

/** One match and whether only a typo made it one. */
export interface RankedDestination {
  destination: Destination;
  /** Matched by the one-typo tier alone; a learned pick never is. */
  fuzzy: boolean;
}

/**
 * Every destination matching `query`, best first, catalog order within a rank
 * (see `destinationMatch.ts` for the tiers). A destination this device picked
 * for a query `query` begins ranks above every other match, newest pick first,
 * even when the text alone would not match it. All areas is the launcher, not
 * a place to go from a search — it is never returned.
 *
 * @param picks - The remembered Go-box picks; defaults to this device's.
 */
export function rankDestinations(
  query: string,
  picks: readonly GoPick[] = loadGoPicks()
): RankedDestination[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const learned = learnedIds(q, picks);
  const ranked: { d: Destination; rank: number; index: number }[] = [];
  DESTINATIONS.forEach((d, index) => {
    if (d.id === 'areas') return;
    const pick = learned.indexOf(d.id);
    const rank = pick >= 0 ? pick - learned.length : matchRank(d, q);
    if (rank !== undefined) ranked.push({ d, rank, index });
  });
  ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);
  return ranked.map(r => ({ destination: r.d, fuzzy: r.rank === TYPO_RANK }));
}
